// The Dropok page's pure helpers (portal-onboarding-drops.md §3–4). Products
// come from the manual upload only (CEO): POST /brands/:id/products/manual,
// then POST /drops/batch with that product's `products.id`.

export const DROP_STATUSES = ['scheduled', 'active', 'completed', 'cancelled'];
export const DROPPABLE_STATUSES = ['DRAFT', 'UNLISTED'];

/// A drop is live for 24 hours from `drop_date` (the API's cron then
/// completes it; a manual product has no stock, so it never sells out).
export const DROP_LIVE_MS = 24 * 60 * 60 * 1000;

/// The earliest start the form offers: a few minutes from now, so the API's
/// DROP_DATE_IN_PAST check never trips on a slow submit.
export const DROP_MIN_LEAD_MS = 5 * 60 * 1000;

/// A list in whichever envelope it came: a bare array, `{ data }` or
/// `{ products }`.
export function listOf(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.products)) return res.products;
  return [];
}

/// Does this drop name this product? A manual product's drop names it by
/// `products.id`, a Shopify product's by its Shopify id.
export function dropNamesProduct(drop, product) {
  if (!drop || !product) return false;
  const id = String(drop.product_id ?? '');
  if (!id) return false;
  return id === String(product.id) || (product.shopify_product_id != null && id === String(product.shopify_product_id));
}

/// Product ids taken by a scheduled or active drop.
export function busyProductIds(drops) {
  return new Set(
    (drops || [])
      .filter((d) => d.status === 'scheduled' || d.status === 'active')
      .map((d) => String(d.product_id))
  );
}

export function productStatus(p) {
  return String(p?.status || p?.product_status || '').toUpperCase();
}

/// Can this product go into a new drop? Clothing (the AI said so), DRAFT or
/// UNLISTED, and not already in a scheduled / active drop.
export function isDroppable(product, busy = new Set()) {
  if (!product || product.is_clothing !== true) return false;
  if (!DROPPABLE_STATUSES.includes(productStatus(product))) return false;
  if (busy.has(String(product.id))) return false;
  if (product.shopify_product_id != null && busy.has(String(product.shopify_product_id))) return false;
  return true;
}

/// The uploaded products still waiting for a drop (newest first, as listed).
export function manualProductsForDrop(products, drops) {
  const busy = busyProductIds(drops);
  return listOf(products).filter((p) => p.source === 'portal_manual' && isDroppable(p, busy));
}

/// What the AI made of a product: the upload / edit answer (`labels` with the
/// vision labels, returned only there; `usable` / `reason`, drops v2) or a
/// product row from the list.
///   state: 'pending' | 'clothing' | 'notClothing' | 'failed'
export function analysisOf(product, labels, answer) {
  const p = product || {};
  const l = labels || {};
  const raw = String(l.labeling_status || p.ai_labeling?.status || '').toUpperCase();
  const isClothing = answer?.isClothing ?? l.is_clothing ?? p.is_clothing ?? null;
  let state;
  if (answer?.reason === 'NOT_CLOTHING') state = 'notClothing';
  else if (answer?.reason === 'LABELING_PENDING') state = 'pending';
  else if (answer?.usable === true) state = 'clothing';
  else if (raw === 'PENDING' || (raw === '' && isClothing == null)) state = 'pending';
  else if (raw === 'FAILED' && isClothing == null) state = 'failed';
  else if (isClothing === true) state = 'clothing';
  else if (isClothing === false) state = 'notClothing';
  else state = raw === 'FAILED' ? 'failed' : 'pending';
  const vision = l.vision && typeof l.vision === 'object' ? l.vision : null;
  const VISION_ORDER = ['type', 'color', 'pattern', 'material', 'fit', 'length'];
  const tags = vision
    ? VISION_ORDER.filter((k) => typeof vision[k] === 'string' && vision[k].trim()).map((k) => ({ key: k, value: vision[k].trim() }))
    : [];
  return {
    state,
    usable: state === 'clothing',
    category: l.clothing_category || p.clothing_category || p.ai_labeling?.category || null,
    style: l.style_category || p.style_category || null,
    description: l.ai_description || p.ai_description || null,
    tags,
    indexed: l.indexed === true,
  };
}

// ── Drops v2: drop groups (§4) ───────────────────────────────────────────────

export const GROUP_NAME_MAX = 120;
export const GROUP_DESCRIPTION_MAX = 2000;
export const GROUP_PRODUCTS_MAX = 50;

/// When the drop closes: 24 hours after it went live (the start while it has
/// not), or the end a drop made earlier was given.
export function groupEnd(g) {
  if (g?.ends_at) return new Date(g.ends_at);
  const from = g?.activated_at || g?.starts_at;
  return from ? dropEnd(from) : null;
}

/// Product ids held by another scheduled or live drop (not `exceptGroupId`).
export function productsInOtherDrops(groups, exceptGroupId = null) {
  const out = new Set();
  (groups || []).forEach((g) => {
    if (g.id === exceptGroupId || !['scheduled', 'active'].includes(g.status)) return;
    (g.products || []).forEach((r) => {
      if (r.status === 'scheduled' || r.status === 'active') out.add(String(r.product_id));
    });
  });
  return out;
}

/// The uploaded products that can go into a drop now (clothing, DRAFT /
/// UNLISTED, not in another open drop).
export function pickableProducts(products, groups, exceptGroupId = null, chosen = []) {
  const busy = productsInOtherDrops(groups, exceptGroupId);
  const taken = new Set(chosen.map(String));
  return listOf(products).filter((p) => p.source === 'portal_manual' && isDroppable(p, busy) && !taken.has(String(p.id)));
}

/// The drop form → { errors, ok }.
///   name required (≤120), description ≤2000, 1–50 usable products, a
///   start in the future (only while it can still change). No end: a drop
///   always lives 24 hours from its start.
export function validateGroupForm({ name, description, products, start, startEditable = true }, now = Date.now()) {
  const errors = {};
  const n = String(name || '').trim();
  if (!n) errors.name = 'required';
  else if (n.length > GROUP_NAME_MAX) errors.name = 'tooLong';
  if (String(description || '').trim().length > GROUP_DESCRIPTION_MAX) errors.description = 'tooLong';
  const list = products || [];
  if (list.length === 0) errors.products = 'required';
  else if (list.length > GROUP_PRODUCTS_MAX) errors.products = 'tooMany';
  else if (list.some((x) => x.state === 'pending')) errors.products = 'pending';
  else if (list.some((x) => x.state !== 'clothing')) errors.products = 'notClothing';
  if (startEditable && !startValid(start, now)) errors.start = start ? 'past' : 'required';
  return { errors, ok: Object.keys(errors).length === 0 };
}

/// The fields of an edit that changed (PUT sends only those).
export function groupChanges(group, next) {
  const out = {};
  if (next.name.trim() !== (group.name || '')) out.name = next.name;
  if ((next.description || '').trim() !== (group.description || '')) out.description = next.description;
  if (next.startsAt && new Date(next.startsAt).getTime() !== new Date(group.starts_at).getTime()) out.startsAt = next.startsAt;
  const oldIds = (group.products || []).map((r) => String(r.product_id));
  if (next.productIds && next.productIds.map(String).join() !== oldIds.join()) out.productIds = next.productIds;
  return out;
}

/// A group's product row → the shape the form's product list uses.
export function rowAsProduct(r) {
  return {
    id: r.product_id,
    name: r.name,
    images: (r.images || []).map((src) => ({ src })),
    price_data: { price: r.price, currency: r.currency },
    status: r.product_status,
    source: r.source,
    is_clothing: true,
  };
}

export function productImageUrl(p) {
  const imgs = Array.isArray(p?.images) ? p.images : [];
  const first = imgs[0];
  return (first && (first.src || (typeof first === 'string' ? first : null))) || p?.image || null;
}

export function dropImageUrl(d) {
  const imgs = Array.isArray(d?.product_images) ? d.product_images : [];
  const first = imgs[0];
  return (first && (typeof first === 'string' ? first : first.src)) || null;
}

/// <input type="datetime-local"> value in local time.
export function toLocalInputValue(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function dropEnd(dropDate) {
  const d = new Date(dropDate);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getTime() + DROP_LIVE_MS);
}

export function startValid(localValue, now = Date.now()) {
  if (!localValue) return false;
  const ts = new Date(localValue).getTime();
  return Number.isFinite(ts) && ts > now;
}

export function fmtDateTime(value, lang) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(lang === 'hu' ? 'hu-HU' : 'en-GB', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function fmtMoney(amount, currency, lang) {
  const v = Number(amount);
  if (!Number.isFinite(v) || v <= 0) return null;
  const cur = String(currency || 'HUF').toUpperCase();
  try {
    return new Intl.NumberFormat(lang === 'hu' ? 'hu-HU' : 'en-GB', {
      style: 'currency', currency: cur, maximumFractionDigits: cur === 'HUF' ? 0 : 2,
    }).format(v);
  } catch {
    return `${v.toLocaleString()} ${cur}`;
  }
}

/// The batch answer → { created, skipped: [{ product_id, reason }] }.
export function batchResult(res) {
  const r = res && typeof res === 'object' && 'created' in res ? res : res?.data || {};
  return { created: Array.isArray(r.created) ? r.created : [], skipped: Array.isArray(r.skipped) ? r.skipped : [] };
}
